import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { Store } from '@ngrx/store';

import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { RouteConfig } from '@app/core/models';
import { Permissions, selectUserContext } from '@app/modules/authentication/store/auth';
import { selectOrganization } from '@app/modules/core/store/user-profile';
import { IconTypes } from '@app/shared/components/icon';
import { filter, map, startWith } from 'rxjs/operators';
import { getRouteData } from '../../utils/get-route-data';
import { NavItem, NavItemComponent } from './nav-item';
import { LocalStorageService } from '@app/core/services';

const getRouteConfigSetting = (route: ActivatedRouteSnapshot) => {
  const { routeConfig = {} } = getRouteData(route) ?? {};
  return routeConfig?.hideNavigation;
};

const STORAGE_KEY = 'topo_side-nav-expanded';

@Component({
    selector: 'topo-app-side-nav',
    imports: [NavItemComponent],
    templateUrl: './app-side-nav.component.html',
    styleUrl: './app-side-nav.component.scss'
})
export class AppSideNavComponent {
  private storage = inject(LocalStorageService);

  isHeaderVisible = input(false);

  isExpanded = signal<boolean>(this.storage.getItem(STORAGE_KEY) ?? true);

  toggleExpansion(): void {
    this.isExpanded.update(state => !state);
  }

  storeExpansion$ = effect(() => {
    const isExpanded = this.isExpanded();
    this.storage.setItem(STORAGE_KEY, isExpanded);
  });

  private store = inject(Store);

  private route = inject(ActivatedRoute);
  private hideNavigation$ = inject(Router).events.pipe(
    filter(event => event instanceof NavigationEnd),
    map(() => getRouteConfigSetting(this.route.snapshot)),
    startWith(getRouteConfigSetting(this.route.snapshot)) // needs this for when user is on org page and toggles the navigation setting
  );
  private hide = toSignal<RouteConfig['hideNavigation']>(this.hideNavigation$);
  protected show = computed(() => {
    const hideNav = this.hide() ?? false;
    // default to showing navigation unless specified by route config
    return !(typeof hideNav === 'function' ? hideNav() : hideNav);
  });

  private userContext = this.store.selectSignal(selectUserContext);
  private userOrg = this.store.selectSignal(selectOrganization);
  private selectedOrgUId = signal<string>(null);

  private enableBusinessUnitViewOnUI = computed(() => {
    const userOrg = this.userOrg();
    return userOrg?.configurationSettings?.enableBusinessUnitViewOnUI;
  });

  navItems = computed<NavItem[]>(() => {
    const items: NavItem[] = [];
    const userContext = this.userContext();

    if (!userContext) {
      return items;
    }

    // Operating Picture
    if (userContext.hasPermission(Permissions.MapRead)) {
      items.push({
        id: 'map',
        label: 'MANAGEMENT.MENU.OPERATING-PICTURE',
        route: '/cop',
        iconProps: {
          type: IconTypes.SystemIcon,
          path: 'icon-map_object'
        }
      });
    }

    // Communications
    if (userContext.hasPermission(Permissions.MassCommunicationsRead)) {
      const commsRoutes: Array<NavItem> = [];

      if (userContext.hasPermission(Permissions.CommunicationNotificationRead)) {
        commsRoutes.push({
          id: 'communications_notifications',
          label: 'COMMUNICATIONS.TAB-NOTIFICATIONS',
          route: '/communications/notifications'
        });
      }

      if (userContext.hasPermission(Permissions.ChannelConfigRead)) {
        commsRoutes.push({
          id: 'communications_channels',
          label: 'COMMUNICATIONS.TAB-CHANNELS',
          route: '/communications/channels'
        });
      }

      if (userContext.hasPermission(Permissions.DistributionListRead)) {
        commsRoutes.push({
          id: 'communications_distribution-lists',
          label: 'COMMUNICATIONS.TAB-DISTRIBUTION-LISTS',
          route: '/communications/distribution-lists'
        });
      }

      if (userContext.hasPermission(Permissions.CommunicationTemplateRead)) {
        commsRoutes.push({
          id: 'communications_templates',
          label: 'COMMUNICATIONS.TAB-TEMPLATES',
          route: '/communications/templates'
        });
      }

      if (userContext.hasPermission(Permissions.WorkflowRead)) {
        commsRoutes.push({
          id: 'communications_workflows',
          label: 'MANAGEMENT.WORKFLOW.WORKFLOW-TEMPLATES',
          route: '/communications/workflows'
        });
      }

      if (commsRoutes.length) {
        items.push({
          id: 'communications',
          label: 'MANAGEMENT.MENU.COMMUNICATIONS',
          route: '/communications',
          iconProps: {
            type: IconTypes.SystemIcon,
            path: 'icon-bullhorn'
          },
          children: commsRoutes
        });
      }
    }

    // Communication Center
    if (
      !userContext.hasPermission(Permissions.MassCommunicationsRead) &&
      (userContext.hasPermission(Permissions.ChannelConfigCreate) ||
        userContext.hasPermission(Permissions.DistributionListCreate) ||
        userContext.hasPermission(Permissions.CommunicationTemplateCreate))
    ) {
      const children: NavItem[] = [];

      if (userContext.hasPermission(Permissions.ChannelConfigCreate)) {
        children.push({
          id: 'communication_center-channels',
          label: 'MANAGEMENT.MENU.CHANNELS',
          route: '/management/communication-center/channels'
        });
      }

      if (userContext.hasPermission(Permissions.DistributionListCreate)) {
        children.push({
          id: 'communication_center-distribution_lists',
          label: 'MANAGEMENT.MENU.DISTRIBUTION-LISTS',
          route: '/management/communication-center/distribution-lists'
        });
      }

      if (userContext.hasPermission(Permissions.CommunicationTemplateCreate)) {
        children.push({
          id: 'communication_center-communication_templates',
          label: 'MANAGEMENT.MENU.TEMPLATES',
          route: '/management/communication-center/templates'
        });
      }

      items.push({
        id: 'communication_center',
        label: 'MANAGEMENT.MENU.COMMUNICATIONS',
        route: '/management/communication-center',
        iconProps: {
          type: IconTypes.SystemIcon,
          path: 'icon-satellite-dish'
        },
        children
      });
    }

    // Analytics
    if (userContext.hasPermission(Permissions.AnalyticsRead)) {
      items.push({
        id: 'analytics',
        label: 'MANAGEMENT.MENU.ANALYTICS',
        route: '/analytics',
        iconProps: {
          type: IconTypes.SystemIcon,
          path: 'icon-chart'
        }
      });
    }

    // Incidents
    if (userContext.hasPermission(Permissions.IncidentRead)) {
      items.push({
        id: 'incidents',
        label: 'MANAGEMENT.MENU.INCIDENTS',
        route: '/incidents',
        iconProps: {
          type: IconTypes.SystemIcon,
          path: 'icon-briefcase'
        }
      });
    }

    // Configuration (formerly Data Boss section)
    if (
      userContext.hasPermission(Permissions.PipelineCreate) ||
      userContext.hasPermission(Permissions.DatasetCreate) ||
      userContext.hasPermission(Permissions.WorkflowCreate) ||
      userContext.hasPermission(Permissions.RatingScalesRead) ||
      userContext.hasPermission(Permissions.MapUpdate)
    ) {
      const children: NavItem[] = [];

      if (userContext.hasPermission(Permissions.DatasetCreate)) {
        children.push({
          id: 'datasets',
          label: 'MANAGEMENT.MENU.DATASETS',
          route: '/management/datasets'
        });
      }

      if (userContext.hasPermission(Permissions.PipelineCreate)) {
        children.push({
          id: 'pipelines',
          label: 'MANAGEMENT.MENU.PIPELINES',
          route: '/management/datafeeds'
        });
      }

      if (userContext.hasPermission(Permissions.WorkflowCreate)) {
        children.push({
          id: 'workflow_statuses',
          label: 'MANAGEMENT.MENU.ALERT-STATUSES',
          route: '/management/status'
        });
      }

      if (userContext.hasPermission(Permissions.RatingScalesRead)) {
        children.push({
          id: 'rating_scales',
          label: 'MANAGEMENT.MENU.RATING-SCALES',
          route: '/management/ratings'
        });
      }

      if (userContext.hasPermission(Permissions.MapUpdate)) {
        children.push({
          id: 'map_permissions',
          label: 'MANAGEMENT.MENU.MAP-PERMISSIONS',
          route: '/management/maps'
        });
      }

      items.push({
        id: 'configuration',
        label: 'MANAGEMENT.MENU.CONFIGURATION',
        route: '/management',
        iconProps: {
          type: IconTypes.SystemIcon,
          path: 'icon-dataset'
        },
        children
      });
    }

    // Admin
    if (
      userContext.hasPermission(Permissions.OrganizationRead) ||
      userContext.hasPermission(Permissions.UserProfileCreate) ||
      userContext.hasPermission(Permissions.SecurityGroupCreate)
    ) {
      const children: NavItem[] = [];

      if (userContext.hasPermission(Permissions.UserProfileCreate)) {
        children.push({
          id: 'users',
          label: 'MANAGEMENT.MENU.USERS',
          route: '/management/security/users'
        });

        children.push({
          id: 'service_accounts',
          label: 'MANAGEMENT.MENU.SERVICE-ACCOUNTS',
          route: '/management/security/service-accounts'
        });
      }

      if (userContext.hasPermission(Permissions.SecurityGroupCreate)) {
        children.push({
          id: 'security_groups',
          label: 'MANAGEMENT.MENU.GROUPS',
          route: '/management/security/groups'
        });

        if (this.enableBusinessUnitViewOnUI()) {
          children.push({
            id: 'business_units',
            label: 'MANAGEMENT.MENU.BUSINESS-UNITS',
            route: '/management/security/business-units'
          });
        }
      }

      if (userContext.hasPermission(Permissions.OrganizationUpdate)) {
        children.push({
          id: 'organizations',
          label: 'MANAGEMENT.MENU.ORGANIZATIONS',
          route: 'management/organizations'
        });
      }

      items.push({
        id: 'admin',
        label: 'MANAGEMENT.MENU.ADMIN',
        route: '/management',
        iconProps: {
          type: IconTypes.SystemIcon,
          path: 'icon-hierarchy'
        },
        children
      });
    }

    // Community
    if (userContext.hasPermission(Permissions.OrganizationLinksCreate)) {
      items.push({
        id: 'community',
        label: 'MANAGEMENT.MENU.COMMUNITY',
        route: '/management/community',
        iconProps: {
          type: IconTypes.SystemIcon,
          path: 'icon-handshake-1'
        }
      });
    }

    // Horizon SSO
    if (this.userOrg()?.configurationSettings?.horizonSSORedirectEnabled) {
      items.push({
        id: 'horizon_sso',
        label: 'MANAGEMENT.MENU.HORIZON'
      });
    }

    return items;
  });
}
